import React, { useEffect, useRef, useState } from "react";
import { Box, Grid, Typography, Container } from "@mui/material";
import { useParams } from "react-router-dom";
import Colors from "assets/Style/Colors";
import { ErrorToaster } from "components/Toaster";
import { CommaSeparator } from "utils";
import carsLogo from "assets/Images/carslogo.svg";
import { FontFamily } from "assets";
import { makeStyles } from "@mui/styles";
import moment from "moment";
import CurrencyServices from "services/Currency";
import { QRCodeCanvas } from "qrcode.react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const useStyle = makeStyles({
  headingBg: {
    margin: "20px 0px",
    padding: "12px 0px",
    textAlign: "center",
  },
  heading: {
    color: Colors.white,
    textTransform: "uppercase",
    fontWeight: 300,
    fontFamily: FontFamily.NunitoRegular,
  },
  text: {
    color: Colors.smokeyGrey,
    fontWeight: 300,
    fontFamily: FontFamily.NunitoRegular,
  },
  textColor: {
    color: Colors.green,
    fontWeight: 300,
    fontFamily: FontFamily.NunitoRegular,
  },
  tableCell: {
    backgroundColor: Colors.aliceBlue,
    border: "0.25px solid #D9D9D9",
    "& .MuiTypography-root": {
      padding: "4px 12px",
    },
  },
  header: {
    backgroundColor: "#0095C8",
    color: "#fff",
    fontWeight: "bold",
    textAlign: "center",
    padding: "12px 0",
    fontSize: "18px",
  },
  tablesCell: {
    padding: "8px 15px",
    borderBottom: "1px solid #B5B5B5",
    fontFamily: "Arial, sans-serif",
  },
  computationRow: {
    backgroundColor: "#F3F3F3",
    padding: "8px",
  },
  computationHeader: {
    backgroundColor: "#0095C8",
    color: "#fff",
    padding: "12px 0",
    textAlign: "center",
    fontSize: "18px",
  },
  gridItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  totalRow: {
    backgroundColor: "#e5f5f9",
    fontWeight: "bold",
  },
});

function ExportShippingInvoiceV2({ invoiceDetail, onDone }) {
  const { id } = useParams();
  const contentRef = useRef(null);
  const classes = useStyle();
  const [loader, setLoader] = useState(true);

  // *For Invoice Detail
  const [payment, setPayment] = useState([]);
  const [layoutSelection, setLayoutSelection] = useState({
    text: "A4",
    value: "size-a4",
  });
  // *For Currencies
  const [currencyExchangeRate, setCurrencyExchangeRate] = useState();
  const [isContentReady, setIsContentReady] = useState(false);

  // *For Sum
  const [sum, setSum] = useState();
  const [customAndVat, setCustomAndVat] = useState();

  useEffect(() => {
    if (invoiceDetail) {
      setPayment(invoiceDetail?.payment);
      let costing = invoiceDetail?.costing;
      setSum(
        parseFloat(costing?.shipping_charges) +
          parseFloat(costing?.broker_fee) +
          parseFloat(costing?.category_a) +
          parseFloat(costing?.late_fee) +
          parseFloat(costing?.ship_late_fee || 0) +
          parseFloat(costing?.other_charge) +
          parseFloat(costing?.storage) +
          parseFloat(costing?.terminal_storages || 0) +
          parseFloat(costing?.title_fee)
      );
      getCurrencies(invoiceDetail?.currency);

      const custom_vat_filter = invoiceDetail?.payments?.filter((i) => {
        return (
          parseFloat(i?.applied_amount) ===
          parseFloat(costing?.custom_duty) + parseFloat(costing?.vat)
        );
      });
      setCustomAndVat(custom_vat_filter);
    }
  }, [invoiceDetail]);

  const totalVehicleUSD = invoiceDetail?.booking?.paymentDetails?.reduce(
    (acc, curr) => acc + parseFloat(curr?.amount || 0),
    0
  );

  const cutomAndShipTotal = parseFloat(invoiceDetail?.total).toFixed(2);
  const FinalTotal = totalVehicleUSD + cutomAndShipTotal;

  // *For Get Currencies
  const getCurrencies = async (currency) => {
    try {
      let params = {
        detailed: true,
      };
      const { data } = await CurrencyServices.getCurrencies(params);

      const rate = data?.currencies.find(
        (e) => e.currency === currency
      )?.conversion_rate;

      setCurrencyExchangeRate(data.currencies[2].conversion_rate);
    } catch (error) {
      ErrorToaster(error);
    }
  };

  const arrivedGalaxyDate =
    invoiceDetail?.costing?.vehicle?.arrived_galaxy_date;
  const extendedDueDate = arrivedGalaxyDate
    ? moment(arrivedGalaxyDate).add(15, "days").format("D-M-YY")
    : "-";

  useEffect(() => {
    if (!invoiceDetail || currencyExchangeRate == null) return;

    setTimeout(async () => {
      const node = contentRef.current;
      if (!node) return;

      await document.fonts.ready;

      const canvas = await html2canvas(node, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#fff",
      });

      const imgData = canvas.toDataURL("image/jpeg", 0.7);

      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "pt",
        format: [999.96, 1186.56],
        compress: true,
      });

      pdf.addImage(imgData, "PNG", 0, 0, 999.96, 1186.56);

      const blob = pdf.output("blob");
      const file = new File(
        [blob],
        `ShippingInvoiceV2-${invoiceDetail?.booking?.vin}.pdf`,
        { type: "application/pdf" }
      );

      onDone?.(file);
    }, 600);
  }, [currencyExchangeRate]);

  return (
    <Container>
      <div ref={contentRef}>
        <Box className={`pdf-page ${layoutSelection.value}`}>
          <Box>
            <Grid container spacing={0}>
              <Box
                sx={{
                  width: { xs: "100%", md: "25%" },
                  "@media print": {
                    width: "25%",
                    flex: "0 0 25%",
                  },
                  marginTop: "5px",
                }}
              >
                <Box
                  component="img"
                  src={carsLogo}
                  alt="Ship Logo"
                  sx={{
                    width: "100%",
                    objectFit: "contain",
                    display: "block",
                    maxHeight: "100px",
                  }}
                />
              </Box>

              {/* Right Contact Info */}
              <Box
                sx={{
                  backgroundColor: Colors.green,
                  padding: 2,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  width: { xs: "100%", md: "71.5%" },
                  marginRight: "3.5%",
                  "@media print": {
                    width: "71.5%",
                    flex: "0 0 71.5%",
                  },
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={5} sx={{ marginLeft: "50px" }}>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                      <Grid item xs={5}>
                        <Typography
                          variant="body1"
                          sx={{ color: Colors.white }}
                        >
                          Invoice No.
                        </Typography>
                      </Grid>
                      <Grid item xs={5}>
                        <Typography
                          variant="body1"
                          sx={{ color: Colors.white }}
                        >
                          GSI-{id}
                        </Typography>
                      </Grid>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={5} sx={{ marginLeft: "50px" }}>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                      <Grid item xs={5}>
                        <Typography
                          variant="body1"
                          sx={{ color: Colors.white }}
                        >
                          Updated Date:
                        </Typography>
                      </Grid>
                      <Grid item xs={5}>
                        <Typography
                          variant="body1"
                          sx={{ color: Colors.white }}
                        >
                          {moment(
                            invoiceDetail?.costing?.vehicle?.arrived_port_date
                              ? invoiceDetail?.costing?.vehicle
                                  ?.arrived_port_date
                              : invoiceDetail?.costing?.updatedAt
                          ).format("D-M-YY")}
                        </Typography>
                      </Grid>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={5} sx={{ marginLeft: "50px" }}>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                      <Grid item xs={5}>
                        <Typography
                          variant="body1"
                          sx={{ color: Colors.white }}
                        >
                          Purchased:
                        </Typography>
                      </Grid>
                      <Grid item xs={5}>
                        <Typography
                          variant="body1"
                          sx={{ color: Colors.white }}
                        >
                          {moment(
                            invoiceDetail?.booking?.purchase_date ?? "-"
                          ).format("D-M-YY")}

                          {/* {moment(
                                                            invoiceDetail?.costing?.vehicle?.arrived_port_date ? invoiceDetail?.costing?.vehicle?.arrived_port_date : invoiceDetail?.costing?.createdAt
                                                        ).format("D-M-YY")} */}
                        </Typography>
                      </Grid>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={5} sx={{ marginLeft: "50px" }}>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                      <Grid item xs={5}>
                        <Typography
                          variant="body1"
                          sx={{ color: Colors.white }}
                        >
                          Shipping Due:
                        </Typography>
                      </Grid>
                      <Grid item xs={5}>
                        <Typography
                          variant="body1"
                          sx={{ color: Colors.white }}
                        >
                          {invoiceDetail?.costing?.vehicle?.arrived_galaxy_date
                            ? moment(
                                invoiceDetail?.costing?.vehicle
                                  ?.arrived_galaxy_date
                              )
                                .add(15, "days")
                                .format("D-M-YY")
                            : "-"}
                        </Typography>
                      </Grid>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
            <Box sx={{ padding: 4 }}>
              <Box
                sx={{
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  overflow: "hidden",
                  width: "100%",
                  fontSize: "13px",
                  marginBottom: "10px",
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    display: "flex",
                    height: 40,
                    width: "100%",
                  }}
                >
                  {/* Light Green Triangle that fills the clipped corner */}
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: "calc(50% - 15%)", // aligns with the diagonal cut start
                      width: "15%",
                      height: "100%",
                      backgroundColor: "#6cc04a",
                      clipPath: "polygon(100% 0, 100% 100%, 0% 100%)", // triangle shape
                      zIndex: 0,
                    }}
                  />

                  {/* Left Header with diagonal cut */}
                  <Box
                    sx={{
                      width: "50%",
                      backgroundColor: "#0a5b2d",
                      clipPath: "polygon(0 0, 100% 0, 85% 100%, 0% 100%)",
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: "bold",
                      fontSize: "14px",
                      zIndex: 1,
                    }}
                  >
                    Customer Details
                  </Box>

                  {/* Right Header */}
                  <Box
                    sx={{
                      width: "50%",
                      backgroundColor: "#6cc04a",
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: "bold",
                      fontSize: "14px",
                      zIndex: 1,
                    }}
                  >
                    Vehicle Details
                  </Box>
                </Box>

                {/* Body */}
                <Grid container>
                  {/* Customer Details */}
                  <Grid item xs={6} sx={{ px: 2, py: 1.5 }}>
                    <Grid container sx={{ padding: "10px" }}>
                      <Grid item xs={5}>
                        <Typography variant="body1">Customer ID:</Typography>
                      </Grid>
                      <Grid item xs={7}>
                        <Typography
                          variant="body1"
                          sx={{ fontFamily: FontFamily.NunitoRegular }}
                        >
                          {invoiceDetail?.customer?.id ?? "-"}{" "}
                        </Typography>
                      </Grid>
                      <Grid item xs={5}>
                        <Typography variant="body1">Customer Name:</Typography>
                      </Grid>
                      <Grid item xs={7}>
                        <Typography
                          variant="body1"
                          sx={{ fontFamily: FontFamily.NunitoRegular }}
                        >
                          {invoiceDetail?.booking?.customer?.name ?? "-"}
                        </Typography>
                      </Grid>
                      <Grid item xs={5}>
                        <Typography variant="body1">Received By:</Typography>
                      </Grid>
                      <Grid item xs={7}>
                        <Typography
                          variant="body1"
                          sx={{ fontFamily: FontFamily.NunitoRegular }}
                        >
                          {invoiceDetail?.payments?.length
                            ? invoiceDetail.payments.at(-1)?.application
                                ?.received_by || "-"
                            : "-"}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>

                  {/* Divider */}
                  <Box
                    sx={{
                      width: "1px",
                      backgroundColor: "#ccc",
                      height: "auto",
                    }}
                  />

                  {/* Vehicle Details */}
                  <Grid item xs={5.9} sx={{ px: 2, py: 1.5 }}>
                    <Grid container spacing={1}>
                      {/* Left column */}
                      <Grid item xs={6}>
                        <Grid container spacing={0.5}>
                          <Grid item xs={5}>
                            <Typography
                              variant="body1"
                              sx={{ fontSize: "16px" }}
                            >
                              Purchased:
                            </Typography>
                          </Grid>
                          <Grid item xs={7}>
                            <Typography
                              variant="body1"
                              sx={{
                                fontSize: "16px",
                                fontFamily: FontFamily.NunitoRegular,
                              }}
                            >
                              {moment(
                                invoiceDetail?.booking?.purchase_date
                              ).format("D-M-YY")}
                            </Typography>
                          </Grid>

                          <Grid item xs={5}>
                            <Typography
                              sx={{ fontSize: "16px" }}
                              variant="body1"
                            >
                              Buyer ID :
                            </Typography>
                          </Grid>
                          <Grid item xs={7}>
                            <Typography
                              variant="body1"
                              sx={{
                                fontSize: "16px",
                                fontFamily: FontFamily.NunitoRegular,
                              }}
                            >
                              {invoiceDetail?.booking?.buyer?.name
                                ? invoiceDetail.booking.buyer.name.includes("-")
                                  ? invoiceDetail.booking.buyer.name.split(
                                      "-"
                                    )[1]
                                  : invoiceDetail.booking.buyer.name
                                : "-"}
                            </Typography>
                          </Grid>

                          <Grid item xs={5}>
                            <Typography
                              sx={{ fontSize: "16px" }}
                              variant="body1"
                            >
                              VIN:
                            </Typography>
                          </Grid>
                          <Grid item xs={7}>
                            <Typography
                              variant="body1"
                              sx={{
                                fontSize: "16px",
                                fontFamily: FontFamily.NunitoRegular,
                              }}
                            >
                              {invoiceDetail?.booking?.vin ?? "-"}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Grid>

                      {/* Right column */}
                      <Grid item xs={6}>
                        <Grid container spacing={0.5}>
                          <Grid item xs={5}>
                            <Typography
                              sx={{ fontSize: "16px" }}
                              variant="body1"
                            >
                              Arrived:
                            </Typography>
                          </Grid>
                          <Grid item xs={7}>
                            <Typography
                              variant="body1"
                              sx={{
                                fontSize: "16px",
                                fontFamily: FontFamily.NunitoRegular,
                              }}
                            >
                              {moment(
                                invoiceDetail?.costing?.vehicle
                                  ?.arrived_port_date
                              ).format("D-M-YY")}
                            </Typography>
                          </Grid>

                          <Grid item xs={5}>
                            <Typography
                              sx={{ fontSize: "16px" }}
                              variant="body1"
                            >
                              LOT:
                            </Typography>
                          </Grid>
                          <Grid item xs={7}>
                            <Typography
                              variant="body1"
                              sx={{
                                fontSize: "16px",
                                fontFamily: FontFamily.NunitoRegular,
                              }}
                            >
                              {invoiceDetail?.booking?.lot_number ?? "-"}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Grid>

                      {/* Full-width row for Make/Model/Color */}
                      <Grid item xs={12}>
                        <Grid container spacing={0.5}>
                          <Grid item xs={4.4}>
                            <Typography
                              sx={{ fontSize: "16px" }}
                              variant="body1"
                            >
                              Model/Make/Color:
                            </Typography>
                          </Grid>
                          <Grid item xs={7.6}>
                            <Typography
                              variant="body1"
                              sx={{
                                fontSize: "16px",
                                fontFamily: FontFamily.NunitoRegular,
                              }}
                            >
                              {invoiceDetail?.booking?.veh_model?.name ?? "-"} -{" "}
                              {invoiceDetail?.booking?.veh_make?.name ?? "-"} -{" "}
                              {invoiceDetail?.booking?.color ?? "-"}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Box>

              <Grid container spacing={2} justifyContent="space-between">
                {/* Left Side - Invoice To Section */}
                <Grid item xs={12} sm={6}>
                  <Grid
                    container
                    sx={{ border: "1px solid #ddd", marginTop: "10px" }}
                  >
                    <Grid item xs={12}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontFamily: "Helvetica",
                          textAlign: "center",
                          fontWeight: "bold",
                          padding: "8px",
                        }}
                      >
                        Vehicle Charges
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          width: "100%",
                        }}
                      >
                        <Box sx={{ overflowX: "auto", maxWidth: "100%" }}>
                          <table
                            style={{
                              width: "100%",
                              borderCollapse: "collapse",
                              margin: 0,
                              padding: 0,
                              tableLayout: "fixed",
                            }}
                          >
                            <thead>
                              <tr
                                style={{
                                  background:
                                    "linear-gradient(90deg, #0C6135 0%, #5BBC0F 100%)",
                                  color: Colors.whiteSmoke,
                                  fontWeight: "bold",
                                }}
                              >
                                <th
                                  style={{
                                    padding: "8px",
                                    textAlign: "center",
                                    borderBottom: "2px solid #ddd",
                                    fontSize: "12px",
                                  }}
                                >
                                  Particular
                                </th>
                                <th
                                  style={{
                                    padding: "8px",
                                    textAlign: "center",
                                    borderBottom: "2px solid #ddd",
                                    fontSize: "12px",
                                  }}
                                >
                                  USD
                                </th>
                                <th
                                  style={{
                                    padding: "8px",
                                    textAlign: "center",
                                    borderBottom: "2px solid #ddd",
                                    fontSize: "12px",
                                  }}
                                >
                                  AED
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td
                                  style={{
                                    padding: "8px",
                                    borderBottom: "1px solid #ddd",
                                    borderRight: "1px solid #ddd",
                                    borderLeft: "1px solid #ddd",
                                    fontSize: "12px",
                                  }}
                                >
                                  Vehicle Charges
                                </td>
                                <td
                                  style={{
                                    padding: "8px",
                                    textAlign: "center",
                                    borderBottom: "1px solid #ddd",
                                    borderRight: "1px solid #ddd",
                                    fontSize: "12px",
                                  }}
                                >
                                  {CommaSeparator(
                                    (
                                      invoiceDetail?.booking?.paymentDetails?.reduce(
                                        (acc, curr) =>
                                          acc + parseFloat(curr?.amount || 0),
                                        0
                                      ) || 0
                                    ).toFixed(2)
                                  )}
                                </td>
                                <td
                                  style={{
                                    padding: "8px",
                                    textAlign: "center",
                                    borderBottom: "1px solid #ddd",
                                    borderRight: "1px solid #ddd",
                                    fontSize: "12px",
                                  }}
                                >
                                  {CommaSeparator(
                                    (
                                      invoiceDetail?.booking?.paymentDetails?.reduce(
                                        (acc, curr) =>
                                          acc +
                                          parseFloat(curr?.amount_aed || 0),
                                        0
                                      ) || 0
                                    ).toFixed(2)
                                  )}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>

                  <Grid
                    container
                    sx={{ border: "1px solid #ddd", marginTop: "10px" }}
                  >
                    <Grid item xs={12}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontFamily: "Helvetica",
                          textAlign: "center",
                          fontWeight: "bold",
                          padding: "8px",
                        }}
                      >
                        Shipping Charges
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          width: "100%",
                        }}
                      >
                        <Box sx={{ overflowX: "auto", maxWidth: "100%" }}>
                          <table
                            style={{
                              width: "100%",
                              borderCollapse: "collapse",
                              margin: 0,
                              padding: 0,
                              tableLayout: "fixed",
                            }}
                          >
                            <thead>
                              <tr
                                style={{
                                  background:
                                    "linear-gradient(90deg, #0C6135 0%, #5BBC0F 100%)",
                                  color: Colors.whiteSmoke,
                                  fontWeight: "bold",
                                }}
                              >
                                <th
                                  style={{
                                    padding: "8px",
                                    textAlign: "center",
                                    borderBottom: "2px solid #ddd",
                                    fontSize: "12px",
                                  }}
                                >
                                  Particular
                                </th>
                                <th
                                  style={{
                                    padding: "8px",
                                    textAlign: "center",
                                    borderBottom: "2px solid #ddd",
                                    fontSize: "12px",
                                  }}
                                >
                                  USD
                                </th>
                                <th
                                  style={{
                                    padding: "8px",
                                    textAlign: "center",
                                    borderBottom: "2px solid #ddd",
                                    fontSize: "12px",
                                  }}
                                >
                                  AED
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {invoiceDetail?.costing?.shipping_charges > 0 && (
                                <tr>
                                  <td
                                    style={{
                                      padding: "8px",
                                      borderBottom: "1px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      borderLeft: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {"Shipping Charge"}
                                  </td>
                                  <td
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      borderBottom: "1px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {invoiceDetail?.total
                                      ? CommaSeparator(
                                          parseFloat(
                                            invoiceDetail?.costing
                                              ?.shipping_charges
                                          )?.toFixed(2)
                                        )
                                      : "00"}
                                  </td>
                                  <td
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      borderBottom: "1px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {invoiceDetail?.total
                                      ? CommaSeparator(
                                          parseFloat(
                                            invoiceDetail?.costing
                                              ?.shipping_charges *
                                              currencyExchangeRate
                                          )?.toFixed(2)
                                        )
                                      : "00"}
                                  </td>
                                </tr>
                              )}
                              {invoiceDetail?.costing?.late_fee > 0 && (
                                <tr>
                                  <td
                                    style={{
                                      padding: "8px",
                                      borderBottom: "1px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      borderLeft: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {"Auction Late Pmt Fee"}
                                  </td>
                                  <td
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      borderBottom: "1px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {invoiceDetail?.paid
                                      ? CommaSeparator(
                                          parseFloat(
                                            invoiceDetail?.costing?.late_fee
                                          )?.toFixed(2)
                                        )
                                      : "00"}
                                  </td>
                                  <td
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      borderBottom: "1px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {invoiceDetail?.paid
                                      ? CommaSeparator(
                                          parseFloat(
                                            invoiceDetail?.costing?.late_fee *
                                              currencyExchangeRate
                                          )?.toFixed(2)
                                        )
                                      : "00"}
                                  </td>
                                </tr>
                              )}
                              {invoiceDetail?.costing?.ship_late_fee > 0 && (
                                <tr>
                                  <td
                                    style={{
                                      padding: "8px",
                                      borderBottom: "1px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      borderLeft: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {"Ship. Late Pmt Fee"}
                                  </td>
                                  <td
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      borderBottom: "1px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {invoiceDetail?.paid
                                      ? CommaSeparator(
                                          parseFloat(
                                            invoiceDetail?.costing?.ship_late_fee
                                          )?.toFixed(2)
                                        )
                                      : "00"}
                                  </td>
                                  <td
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      borderBottom: "1px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {invoiceDetail?.paid
                                      ? CommaSeparator(
                                          parseFloat(
                                            invoiceDetail?.costing
                                              ?.ship_late_fee *
                                              currencyExchangeRate
                                          )?.toFixed(2)
                                        )
                                      : "00"}
                                  </td>
                                </tr>
                              )}
                              {invoiceDetail?.costing?.storage > 0 && (
                                <tr>
                                  <td
                                    style={{
                                      padding: "8px",
                                      borderBottom: "1px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      borderLeft: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {"Storages"}
                                  </td>
                                  <td
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      borderBottom: "1px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {invoiceDetail?.costing
                                      ? CommaSeparator(
                                          parseFloat(
                                            invoiceDetail?.costing?.storage
                                          )?.toFixed(2)
                                        )
                                      : "00"}
                                  </td>
                                  <td
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      borderBottom: "1px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {invoiceDetail?.costing?.storage
                                      ? CommaSeparator(
                                          parseFloat(
                                            invoiceDetail?.costing?.storage *
                                              currencyExchangeRate
                                          )?.toFixed(2)
                                        )
                                      : "00"}
                                  </td>
                                </tr>
                              )}
                              {invoiceDetail?.costing?.terminal_storages > 0 && (
                                <tr>
                                  <td
                                    style={{
                                      padding: "8px",
                                      borderBottom: "1px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      borderLeft: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {"Terminal Storages"}
                                  </td>
                                  <td
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      borderBottom: "1px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {invoiceDetail?.costing
                                      ? CommaSeparator(
                                          parseFloat(
                                            invoiceDetail?.costing
                                              ?.terminal_storages
                                          )?.toFixed(2)
                                        )
                                      : "00"}
                                  </td>
                                  <td
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      borderBottom: "1px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {invoiceDetail?.costing?.terminal_storages
                                      ? CommaSeparator(
                                          parseFloat(
                                            invoiceDetail?.costing
                                              ?.terminal_storages *
                                              currencyExchangeRate
                                          )?.toFixed(2)
                                        )
                                      : "00"}
                                  </td>
                                </tr>
                              )}
                              {invoiceDetail?.costing?.category_a > 0 && (
                                <tr>
                                  <td
                                    style={{
                                      padding: "8px",
                                      borderBottom: "1px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      borderLeft: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {"Category A"}
                                  </td>
                                  <td
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      borderBottom: "1px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {invoiceDetail?.costing
                                      ? CommaSeparator(
                                          parseFloat(
                                            invoiceDetail?.costing?.category_a
                                          )?.toFixed(2)
                                        )
                                      : "00"}
                                  </td>
                                  <td
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      borderBottom: "1px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {invoiceDetail?.costing?.category_a
                                      ? CommaSeparator(
                                          parseFloat(
                                            invoiceDetail?.costing?.category_a *
                                              currencyExchangeRate
                                          )?.toFixed(2)
                                        )
                                      : "00"}
                                  </td>
                                </tr>
                              )}
                              {invoiceDetail?.costing?.broker_fee > 0 && (
                                <tr>
                                  <td
                                    style={{
                                      padding: "8px",
                                      borderBottom: "1px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      borderLeft: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {"Broker Fee"}
                                  </td>
                                  <td
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      borderBottom: "1px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {invoiceDetail?.costing
                                      ? CommaSeparator(
                                          parseFloat(
                                            invoiceDetail?.costing?.broker_fee
                                          )?.toFixed(2)
                                        )
                                      : "00"}
                                  </td>
                                  <td
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      borderBottom: "1px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {invoiceDetail?.costing?.broker_fee
                                      ? CommaSeparator(
                                          parseFloat(
                                            invoiceDetail?.costing?.broker_fee *
                                              currencyExchangeRate
                                          )?.toFixed(2)
                                        )
                                      : "00"}
                                  </td>
                                </tr>
                              )}
                              {invoiceDetail?.costing?.title_fee > 0 && (
                                <tr>
                                  <td
                                    style={{
                                      padding: "8px",
                                      borderBottom: "1px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      borderLeft: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {"Title Fee"}
                                  </td>
                                  <td
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      borderBottom: "1px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {invoiceDetail?.costing
                                      ? CommaSeparator(
                                          parseFloat(
                                            invoiceDetail?.costing?.title_fee
                                          )?.toFixed(2)
                                        )
                                      : "00"}
                                  </td>
                                  <td
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      borderBottom: "1px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {invoiceDetail?.costing?.title_fee
                                      ? CommaSeparator(
                                          parseFloat(
                                            invoiceDetail?.costing?.title_fee *
                                              currencyExchangeRate
                                          )?.toFixed(2)
                                        )
                                      : "00"}
                                  </td>
                                </tr>
                              )}
                              {invoiceDetail?.costing?.current_suv > 0 && (
                                <tr>
                                  <td
                                    style={{
                                      padding: "8px",
                                      borderBottom: "1px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      borderLeft: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {"SUV Charge"}
                                  </td>
                                  <td
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      borderBottom: "1px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {invoiceDetail?.costing
                                      ? CommaSeparator(
                                          parseFloat(
                                            invoiceDetail?.costing?.current_suv
                                          )?.toFixed(2)
                                        )
                                      : "00"}
                                  </td>
                                  <td
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      borderBottom: "1px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {invoiceDetail?.costing?.current_suv
                                      ? CommaSeparator(
                                          parseFloat(
                                            invoiceDetail?.costing
                                              ?.current_suv *
                                              currencyExchangeRate
                                          )?.toFixed(2)
                                        )
                                      : "00"}
                                  </td>
                                </tr>
                              )}
                              {invoiceDetail?.costing?.clearance_att_fee >
                                0 && (
                                <tr>
                                  <td
                                    style={{
                                      padding: "8px",
                                      borderBottom: "1px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      borderLeft: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {"Clear/Attestation Fee"}
                                  </td>
                                  <td
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      borderBottom: "1px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {invoiceDetail?.costing
                                      ? CommaSeparator(
                                          parseFloat(
                                            invoiceDetail?.costing
                                              ?.clearance_att_fee
                                          )?.toFixed(2)
                                        )
                                      : "00"}
                                  </td>
                                  <td
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      borderBottom: "1px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {invoiceDetail?.costing?.clearance_att_fee
                                      ? CommaSeparator(
                                          parseFloat(
                                            invoiceDetail?.costing
                                              ?.clearance_att_fee *
                                              currencyExchangeRate
                                          )?.toFixed(2)
                                        )
                                      : "00"}
                                  </td>
                                </tr>
                              )}
                              {invoiceDetail?.costing?.hybrid_fee > 0 && (
                                <tr>
                                  <td
                                    style={{
                                      padding: "8px",
                                      borderBottom: "1px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      borderLeft: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {"Hybrid/EV Fee"}
                                  </td>
                                  <td
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      borderBottom: "1px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {invoiceDetail?.costing
                                      ? CommaSeparator(
                                          parseFloat(
                                            invoiceDetail?.costing?.hybrid_fee
                                          )?.toFixed(2)
                                        )
                                      : "00"}
                                  </td>
                                  <td
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      borderBottom: "1px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {invoiceDetail?.costing?.hybrid_fee
                                      ? CommaSeparator(
                                          parseFloat(
                                            invoiceDetail?.costing?.hybrid_fee *
                                              currencyExchangeRate
                                          )?.toFixed(2)
                                        )
                                      : "00"}
                                  </td>
                                </tr>
                              )}
                              {invoiceDetail?.costing?.relist_reversal_fee >
                                0 && (
                                <tr>
                                  <td
                                    style={{
                                      padding: "8px",
                                      borderBottom: "1px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      borderLeft: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {"Relist Reversal Fee"}
                                  </td>
                                  <td
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      borderBottom: "1px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {invoiceDetail?.costing
                                      ? CommaSeparator(
                                          parseFloat(
                                            invoiceDetail?.costing
                                              ?.relist_reversal_fee
                                          )?.toFixed(2)
                                        )
                                      : "00"}
                                  </td>
                                  <td
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      borderBottom: "1px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {invoiceDetail?.costing?.relist_reversal_fee
                                      ? CommaSeparator(
                                          parseFloat(
                                            invoiceDetail?.costing
                                              ?.relist_reversal_fee *
                                              currencyExchangeRate
                                          )?.toFixed(2)
                                        )
                                      : "00"}
                                  </td>
                                </tr>
                              )}
                              {invoiceDetail?.costing?.misc_fee > 0 && (
                                <tr>
                                  <td
                                    style={{
                                      padding: "8px",
                                      borderBottom: "1px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      borderLeft: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {"Misc Fee"}
                                  </td>
                                  <td
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      borderBottom: "1px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {invoiceDetail?.costing
                                      ? CommaSeparator(
                                          parseFloat(
                                            invoiceDetail?.costing?.misc_fee
                                          )?.toFixed(2)
                                        )
                                      : "00"}
                                  </td>
                                  <td
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      borderBottom: "1px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {invoiceDetail?.costing?.misc_fee
                                      ? CommaSeparator(
                                          parseFloat(
                                            invoiceDetail?.costing?.misc_fee *
                                              currencyExchangeRate
                                          )?.toFixed(2)
                                        )
                                      : "00"}
                                  </td>
                                </tr>
                              )}
                              {invoiceDetail?.costing?.parts_pickup_fee > 0 && (
                                <tr>
                                  <td
                                    style={{
                                      padding: "8px",
                                      borderBottom: "1px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      borderLeft: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {"Parts Pickup Fee"}
                                  </td>
                                  <td
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      borderBottom: "1px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {invoiceDetail?.costing
                                      ? CommaSeparator(
                                          parseFloat(
                                            invoiceDetail?.costing
                                              ?.parts_pickup_fee
                                          )?.toFixed(2)
                                        )
                                      : "00"}
                                  </td>
                                  <td
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      borderBottom: "1px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {invoiceDetail?.costing?.parts_pickup_fee
                                      ? CommaSeparator(
                                          parseFloat(
                                            invoiceDetail?.costing
                                              ?.parts_pickup_fee *
                                              currencyExchangeRate
                                          )?.toFixed(2)
                                        )
                                      : "00"}
                                  </td>
                                </tr>
                              )}
                              {invoiceDetail?.costing?.heavy_equipment_fee >
                                0 && (
                                <tr>
                                  <td
                                    style={{
                                      padding: "8px",
                                      borderBottom: "1px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      borderLeft: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {"Heavy Equipment Fee"}
                                  </td>
                                  <td
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      borderBottom: "1px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {invoiceDetail?.costing
                                      ? CommaSeparator(
                                          parseFloat(
                                            invoiceDetail?.costing
                                              ?.heavy_equipment_fee
                                          )?.toFixed(2)
                                        )
                                      : "00"}
                                  </td>
                                  <td
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      borderBottom: "1px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {invoiceDetail?.costing?.heavy_equipment_fee
                                      ? CommaSeparator(
                                          parseFloat(
                                            invoiceDetail?.costing
                                              ?.heavy_equipment_fee *
                                              currencyExchangeRate
                                          )?.toFixed(2)
                                        )
                                      : "00"}
                                  </td>
                                </tr>
                              )}
                              {invoiceDetail?.costing?.fuel_surcharge > 0 && (
                                <tr>
                                  <td
                                    style={{
                                      padding: "8px",
                                      borderBottom: "1px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      borderLeft: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {"Fuel Surcharge"}
                                  </td>
                                  <td
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      borderBottom: "1px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {invoiceDetail?.costing
                                      ? CommaSeparator(
                                          parseFloat(
                                            invoiceDetail?.costing
                                              ?.fuel_surcharge
                                          )?.toFixed(2)
                                        )
                                      : "00"}
                                  </td>
                                  <td
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      borderBottom: "1px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {invoiceDetail?.costing?.fuel_surcharge
                                      ? CommaSeparator(
                                          parseFloat(
                                            invoiceDetail?.costing
                                              ?.fuel_surcharge *
                                              currencyExchangeRate
                                          )?.toFixed(2)
                                        )
                                      : "00"}
                                  </td>
                                </tr>
                              )}
                              {invoiceDetail?.costing?.color_change > 0 && (
                                <tr>
                                  <td
                                    style={{
                                      padding: "8px",
                                      borderBottom: "1px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      borderLeft: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {"Color Change"}
                                  </td>
                                  <td
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      borderBottom: "1px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {invoiceDetail?.costing
                                      ? CommaSeparator(
                                          parseFloat(
                                            invoiceDetail?.costing?.color_change
                                          )?.toFixed(2)
                                        )
                                      : "00"}
                                  </td>
                                  <td
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      borderBottom: "1px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {invoiceDetail?.costing?.color_change
                                      ? CommaSeparator(
                                          parseFloat(
                                            invoiceDetail?.costing
                                              ?.color_change *
                                              currencyExchangeRate
                                          )?.toFixed(2)
                                        )
                                      : "00"}
                                  </td>
                                </tr>
                              )}
                              {invoiceDetail?.costing?.thc_charges > 0 && (
                                <tr>
                                  <td
                                    style={{
                                      padding: "8px",
                                      borderBottom: "1px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      borderLeft: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {"THC Charges"}
                                  </td>
                                  <td
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      borderBottom: "1px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {invoiceDetail?.costing
                                      ? CommaSeparator(
                                          parseFloat(
                                            invoiceDetail?.costing?.thc_charges
                                          )?.toFixed(2)
                                        )
                                      : "00"}
                                  </td>
                                  <td
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      borderBottom: "1px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {invoiceDetail?.costing?.thc_charges
                                      ? CommaSeparator(
                                          parseFloat(
                                            invoiceDetail?.costing
                                              ?.thc_charges *
                                              currencyExchangeRate
                                          )?.toFixed(2)
                                        )
                                      : "00"}
                                  </td>
                                </tr>
                              )}
                              {invoiceDetail?.costing?.wrapping_fee > 0 && (
                                <tr>
                                  <td
                                    style={{
                                      padding: "8px",
                                      borderBottom: "1px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      borderLeft: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {"Wrapping Fee"}
                                  </td>
                                  <td
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      borderBottom: "1px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {invoiceDetail?.costing
                                      ? CommaSeparator(
                                          parseFloat(
                                            invoiceDetail?.costing?.wrapping_fee
                                          )?.toFixed(2)
                                        )
                                      : "00"}
                                  </td>
                                  <td
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      textAlign: "center",
                                      borderBottom: "1px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {invoiceDetail?.costing?.wrapping_fee
                                      ? CommaSeparator(
                                          parseFloat(
                                            invoiceDetail?.costing
                                              ?.wrapping_fee *
                                              currencyExchangeRate
                                          )?.toFixed(2)
                                        )
                                      : "00"}
                                  </td>
                                </tr>
                              )}
                              <tr>
                                <td
                                  style={{
                                    padding: "8px",
                                    borderBottom: "1px solid #ddd",
                                    borderRight: "1px solid #ddd",
                                    borderLeft: "1px solid #ddd",
                                    background: "#e0e0e0",
                                    fontWeight: "800px",
                                    fontSize: "12px",
                                  }}
                                >
                                  {"Total Shipping Charges"}
                                </td>
                                <td
                                  style={{
                                    padding: "8px",
                                    textAlign: "center",
                                    borderBottom: "1px solid #ddd",
                                    borderRight: "1px solid #ddd",
                                    fontSize: "12px",
                                  }}
                                >
                                  {invoiceDetail?.costing
                                    ? CommaSeparator(
                                        (
                                          parseFloat(
                                            invoiceDetail?.costing
                                              ?.shipping_charges || 0
                                          ) +
                                          parseFloat(
                                            invoiceDetail?.costing?.late_fee ||
                                              0
                                          ) +
                                          parseFloat(
                                            invoiceDetail?.costing
                                              ?.ship_late_fee || 0
                                          ) +
                                          parseFloat(
                                            invoiceDetail?.costing?.storage || 0
                                          ) +
                                          parseFloat(
                                            invoiceDetail?.costing
                                              ?.terminal_storages || 0
                                          ) +
                                          parseFloat(
                                            invoiceDetail?.costing
                                              ?.category_a || 0
                                          ) +
                                          parseFloat(
                                            invoiceDetail?.costing
                                              ?.broker_fee || 0
                                          ) +
                                          parseFloat(
                                            invoiceDetail?.costing?.title_fee ||
                                              0
                                          ) +
                                          parseFloat(
                                            invoiceDetail?.costing
                                              ?.hybrid_fee || 0
                                          ) +
                                          parseFloat(
                                            invoiceDetail?.costing?.misc_fee ||
                                              0
                                          ) +
                                          parseFloat(
                                            invoiceDetail?.costing
                                              ?.parts_pickup_fee || 0
                                          ) +
                                          parseFloat(
                                            invoiceDetail?.costing
                                              ?.relist_reversal_fee || 0
                                          ) +
                                          parseFloat(
                                            invoiceDetail?.costing
                                              ?.clearance_att_fee || 0
                                          ) +
                                          parseFloat(
                                            invoiceDetail?.costing
                                              ?.wrapping_fee || 0
                                          ) +
                                          parseFloat(
                                            invoiceDetail?.costing
                                              ?.current_suv || 0
                                          ) +
                                          parseFloat(
                                            invoiceDetail?.costing
                                              ?.heavy_equipment_fee || 0
                                          ) +
                                          parseFloat(
                                            invoiceDetail?.costing
                                              ?.fuel_surcharge || 0
                                          ) +
                                          parseFloat(
                                            invoiceDetail?.costing
                                              ?.color_change || 0
                                          ) +
                                          parseFloat(
                                            invoiceDetail?.costing
                                              ?.thc_charges || 0
                                          )
                                        ).toFixed(2)
                                      )
                                    : "00"}
                                </td>
                                <td
                                  style={{
                                    padding: "8px",
                                    textAlign: "center",
                                    borderBottom: "1px solid #ddd",
                                    borderRight: "1px solid #ddd",
                                    fontSize: "12px",
                                  }}
                                >
                                  {(() => {
                                    const totalNumber =
                                      parseFloat(
                                        invoiceDetail?.costing
                                          ?.shipping_charges || 0
                                      ) +
                                      parseFloat(
                                        invoiceDetail?.costing?.late_fee || 0
                                      ) +
                                      parseFloat(
                                        invoiceDetail?.costing?.ship_late_fee ||
                                          0
                                      ) +
                                      parseFloat(
                                        invoiceDetail?.costing?.storage || 0
                                      ) +
                                      parseFloat(
                                        invoiceDetail?.costing
                                          ?.terminal_storages || 0
                                      ) +
                                      parseFloat(
                                        invoiceDetail?.costing?.category_a || 0
                                      ) +
                                      parseFloat(
                                        invoiceDetail?.costing?.broker_fee || 0
                                      ) +
                                      parseFloat(
                                        invoiceDetail?.costing?.title_fee || 0
                                      ) +
                                      parseFloat(
                                        invoiceDetail?.costing?.hybrid_fee || 0
                                      ) +
                                      parseFloat(
                                        invoiceDetail?.costing?.misc_fee || 0
                                      ) +
                                      parseFloat(
                                        invoiceDetail?.costing
                                          ?.parts_pickup_fee || 0
                                      ) +
                                      parseFloat(
                                        invoiceDetail?.costing
                                          ?.relist_reversal_fee || 0
                                      ) +
                                      parseFloat(
                                        invoiceDetail?.costing
                                          ?.clearance_att_fee || 0
                                      ) +
                                      parseFloat(
                                        invoiceDetail?.costing?.wrapping_fee ||
                                          0
                                      ) +
                                      parseFloat(
                                        invoiceDetail?.costing?.current_suv || 0
                                      ) +
                                      parseFloat(
                                        invoiceDetail?.costing
                                          ?.heavy_equipment_fee || 0
                                      ) +
                                      parseFloat(
                                        invoiceDetail?.costing
                                          ?.fuel_surcharge || 0
                                      ) +
                                      parseFloat(
                                        invoiceDetail?.costing?.color_change || 0
                                      ) +
                                      parseFloat(
                                        invoiceDetail?.costing?.thc_charges || 0
                                      );

                                    const totalFixed = totalNumber.toFixed(2);

                                    const shipingBalAED =
                                      totalFixed * currencyExchangeRate;

                                    return (
                                      <>
                                        {CommaSeparator(
                                          shipingBalAED.toFixed(2)
                                        )}
                                      </>
                                    );
                                  })()}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                  <Grid
                    container
                    sx={{ border: "1px solid #ddd", marginTop: "10px" }}
                  >
                    <Grid item xs={12}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontFamily: "Helvetica",
                          textAlign: "center",
                          fontWeight: "bold",
                          padding: "8px",
                        }}
                      >
                        Custom/VAT Charges
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          width: "100%",
                        }}
                      >
                        <Box sx={{ overflowX: "auto", maxWidth: "100%" }}>
                          <table
                            style={{
                              width: "100%",
                              borderCollapse: "collapse",
                              margin: 0,
                              padding: 0,
                              tableLayout: "fixed",
                            }}
                          >
                            <thead>
                              <tr
                                style={{
                                  background:
                                    "linear-gradient(90deg, #0C6135 0%, #5BBC0F 100%)",
                                  color: Colors.whiteSmoke,
                                  fontWeight: "bold",
                                }}
                              >
                                <th
                                  style={{
                                    padding: "8px",
                                    textAlign: "center",
                                    borderBottom: "2px solid #ddd",
                                    fontSize: "12px",
                                  }}
                                >
                                  Particular
                                </th>
                                <th
                                  style={{
                                    padding: "8px",
                                    textAlign: "center",
                                    borderBottom: "2px solid #ddd",
                                    fontSize: "12px",
                                  }}
                                >
                                  USD
                                </th>
                                <th
                                  style={{
                                    padding: "8px",
                                    textAlign: "center",
                                    borderBottom: "2px solid #ddd",
                                    fontSize: "12px",
                                  }}
                                >
                                  AED
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td
                                  style={{
                                    padding: "8px",
                                    borderBottom: "1px solid #ddd",
                                    borderRight: "1px solid #ddd",
                                    borderLeft: "1px solid #ddd",
                                    fontSize: "12px",
                                  }}
                                >
                                  {"Custom Duty"}
                                </td>
                                <td
                                  style={{
                                    padding: "8px",
                                    textAlign: "center",
                                    borderBottom: "1px solid #ddd",
                                    borderRight: "1px solid #ddd",
                                    fontSize: "12px",
                                  }}
                                >
                                  {invoiceDetail?.costing
                                    ? CommaSeparator(
                                        parseFloat(
                                          invoiceDetail?.custom_total
                                        )?.toFixed(2)
                                      )
                                    : "00"}
                                </td>
                                <td
                                  style={{
                                    padding: "8px",
                                    textAlign: "center",
                                    borderBottom: "1px solid #ddd",
                                    borderRight: "1px solid #ddd",
                                    fontSize: "12px",
                                  }}
                                >
                                  {invoiceDetail?.costing
                                    ? CommaSeparator(
                                        parseFloat(
                                          invoiceDetail?.custom_total *
                                            currencyExchangeRate
                                        )?.toFixed(2)
                                      )
                                    : "00"}
                                </td>
                              </tr>
                              <tr>
                                <td
                                  style={{
                                    padding: "8px",
                                    borderBottom: "1px solid #ddd",
                                    borderRight: "1px solid #ddd",
                                    borderLeft: "1px solid #ddd",
                                    fontSize: "12px",
                                  }}
                                >
                                  {"VAT 5%"}
                                </td>
                                <td
                                  style={{
                                    padding: "8px",
                                    textAlign: "center",
                                    borderBottom: "1px solid #ddd",
                                    borderRight: "1px solid #ddd",
                                    fontSize: "12px",
                                  }}
                                >
                                  {invoiceDetail?.costing
                                    ? CommaSeparator(
                                        parseFloat(
                                          invoiceDetail?.vat_total
                                        ).toFixed(2)
                                      )
                                    : "00"}
                                </td>
                                <td
                                  style={{
                                    padding: "8px",
                                    textAlign: "center",
                                    borderBottom: "1px solid #ddd",
                                    borderRight: "1px solid #ddd",
                                    fontSize: "12px",
                                  }}
                                >
                                  {invoiceDetail?.costing
                                    ? CommaSeparator(
                                        parseFloat(
                                          parseFloat(
                                            invoiceDetail?.vat_total *
                                              currencyExchangeRate
                                          ).toFixed(2)
                                        ).toFixed(2)
                                      )
                                    : "00"}
                                </td>
                              </tr>
                              <tr>
                                <td
                                  style={{
                                    padding: "8px",
                                    borderBottom: "1px solid #ddd",
                                    borderRight: "1px solid #ddd",
                                    borderLeft: "1px solid #ddd",
                                    background: "#e0e0e0",
                                    fontWeight: "800px",
                                    fontSize: "12px",
                                  }}
                                >
                                  {"Total Custom & VAT"}
                                </td>
                                <td
                                  style={{
                                    padding: "8px",
                                    textAlign: "center",
                                    borderBottom: "1px solid #ddd",
                                    borderRight: "1px solid #ddd",
                                    fontSize: "12px",
                                  }}
                                >
                                  {invoiceDetail?.costing
                                    ? CommaSeparator(
                                        parseFloat(
                                          parseFloat(invoiceDetail?.vat_total) +
                                            parseFloat(
                                              invoiceDetail?.custom_total
                                            )
                                        ).toFixed(2)
                                      )
                                    : "00"}
                                </td>
                                <td
                                  style={{
                                    padding: "8px",
                                    textAlign: "center",
                                    borderBottom: "1px solid #ddd",
                                    borderRight: "1px solid #ddd",
                                    fontSize: "12px",
                                  }}
                                >
                                  {invoiceDetail?.costing
                                    ? CommaSeparator(
                                        parseFloat(
                                          (parseFloat(
                                            invoiceDetail?.vat_total
                                          ) +
                                            parseFloat(
                                              invoiceDetail?.custom_total
                                            )) *
                                            parseFloat(currencyExchangeRate)
                                        ).toFixed(2)
                                      )
                                    : "00"}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                  <Grid item xs={12} sx={{ marginTop: "10px" }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        width: "100%",
                      }}
                    >
                      <Box sx={{ overflowX: "auto", maxWidth: "100%" }}>
                        <table
                          style={{
                            width: "100%",
                            borderCollapse: "collapse",
                            margin: 0,
                            padding: 0,
                            tableLayout: "fixed",
                          }}
                        >
                          <thead>
                            <tr
                              style={{
                                background:
                                  "linear-gradient(90deg, #0C6135 0%, #5BBC0F 100%)",
                                color: Colors.whiteSmoke,
                                fontWeight: "bold",
                              }}
                            >
                              <th
                                style={{
                                  padding: "8px",
                                  textAlign: "center",
                                  borderBottom: "2px solid #ddd",
                                  fontSize: "12px",
                                }}
                              >
                                Discount
                              </th>

                              <th
                                style={{
                                  padding: "8px",
                                  textAlign: "center",
                                  borderBottom: "2px solid #ddd",
                                  fontSize: "12px",
                                }}
                              >
                                Final Total
                              </th>
                              <th
                                style={{
                                  padding: "8px",
                                  textAlign: "center",
                                  borderBottom: "2px solid #ddd",
                                  fontSize: "12px",
                                }}
                              >
                                Paid Amount
                              </th>
                              <th
                                style={{
                                  padding: "8px",
                                  textAlign: "center",
                                  borderBottom: "2px solid #ddd",
                                  fontSize: "12px",
                                }}
                              >
                                Balance Due
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td
                                style={{
                                  padding: "8px",
                                  borderBottom: "1px solid #ddd",
                                  textAlign: "center",
                                  borderRight: "1px solid #ddd",
                                  borderLeft: "1px solid #ddd",
                                  fontSize: "12px",
                                }}
                              >
                                <div>
                                  $
                                  {CommaSeparator(
                                    parseFloat(
                                      invoiceDetail?.costing?.discount || 0
                                    ).toFixed(2)
                                  )}
                                </div>
                                <div style={{ fontSize: "11px" }}>
                                  AED{" "}
                                  {CommaSeparator(
                                    (
                                      parseFloat(
                                        invoiceDetail?.costing?.discount || 0
                                      ) * currencyExchangeRate
                                    ).toFixed(2)
                                  )}
                                </div>
                              </td>

                              <td
                                style={{
                                  padding: "8px",
                                  borderBottom: "1px solid #ddd",
                                  textAlign: "center",
                                  borderRight: "1px solid #ddd",
                                  fontSize: "12px",
                                }}
                              >
                                {(() => {
                                  const vehicleTotalUSD =
                                    invoiceDetail?.booking?.paymentDetails?.reduce(
                                      (acc, curr) =>
                                        acc + parseFloat(curr?.amount || 0),
                                      0
                                    ) || 0;

                                  const shippingTotal =
                                    invoiceDetail?.reversedBy != null
                                      ? 0
                                      : parseFloat(invoiceDetail?.total || 0);
                                  const finalTotal =
                                    vehicleTotalUSD + shippingTotal;
                                  const finalAED =
                                    finalTotal * currencyExchangeRate;

                                  return (
                                    <>
                                      <div>
                                        ${CommaSeparator(finalTotal.toFixed(2))}
                                      </div>
                                      <div style={{ fontSize: "11px" }}>
                                        AED{" "}
                                        {CommaSeparator(finalAED.toFixed(2))}
                                      </div>
                                    </>
                                  );
                                })()}
                              </td>

                              <td
                                style={{
                                  padding: "8px",
                                  borderBottom: "1px solid #ddd",
                                  textAlign: "center",
                                  borderRight: "1px solid #ddd",
                                  fontSize: "12px",
                                }}
                              >
                                {(() => {
                                  const vehicleTotalUSD =
                                    invoiceDetail?.booking?.paymentDetails?.reduce(
                                      (acc, curr) =>
                                        acc + parseFloat(curr?.amount || 0),
                                      0
                                    ) || 0;

                                  const paidTotal =
                                    invoiceDetail?.payment?.reduce(
                                      (acc, curr) =>
                                        acc + parseFloat(curr?.amount || 0),
                                      0
                                    ) || 0;

                                  const totalPaidAmount =
                                    vehicleTotalUSD + paidTotal;
                                  const totalPaidAED =
                                    totalPaidAmount * currencyExchangeRate;

                                  return (
                                    <>
                                      <div>
                                        $
                                        {CommaSeparator(
                                          totalPaidAmount.toFixed(2)
                                        )}
                                      </div>
                                      <div style={{ fontSize: "11px" }}>
                                        AED{" "}
                                        {CommaSeparator(
                                          totalPaidAED.toFixed(2)
                                        )}
                                      </div>
                                    </>
                                  );
                                })()}
                              </td>

                              <td
                                style={{
                                  padding: "8px",
                                  borderBottom: "1px solid #ddd",
                                  textAlign: "center",
                                  borderRight: "1px solid #ddd",
                                  fontSize: "12px",
                                }}
                              >
                                <div>
                                  $
                                  {CommaSeparator(
                                    parseFloat(
                                      invoiceDetail?.balance || 0
                                    ).toFixed(2)
                                  )}
                                </div>
                                <div style={{ fontSize: "11px" }}>
                                  AED{" "}
                                  {CommaSeparator(
                                    (
                                      parseFloat(invoiceDetail?.balance || 0) *
                                      currencyExchangeRate
                                    ).toFixed(2)
                                  )}
                                </div>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>

                {/* Right Side - Hello Text */}
                <Grid item xs={12} sm={6}>
                  <Grid
                    container
                    spacing={0.5}
                    sx={{ border: "1px solid #ddd", marginTop: "10px" }}
                  >
                    <Grid item xs={12}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontFamily: "Helvetica",
                          textAlign: "center",
                          fontWeight: "bold",
                          padding: "8px",
                        }}
                      >
                        Payment History
                      </Typography>
                    </Grid>
                    <Grid md={12} sx={{ paddingLeft: 0 }}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          width: "100%",
                        }}
                      >
                        <Box
                          sx={{
                            overflowX: "auto",
                            maxWidth: "100%",
                            paddingLeft: 0,
                            marginLeft: 0,
                          }}
                        >
                          <table
                            style={{
                              width: "100%",
                              borderCollapse: "collapse",
                              margin: 0,
                              padding: 0,
                              tableLayout: "fixed",
                            }}
                          >
                            <thead>
                              <tr
                                style={{
                                  background: Colors.green,
                                  color: Colors.whiteSmoke,
                                  fontWeight: "bold",
                                }}
                              >
                                <th
                                  style={{
                                    padding: "8px",
                                    textAlign: "left",
                                    borderBottom: "2px solid #ddd",
                                    fontSize: "12px",
                                    textAlign: "center",
                                  }}
                                >
                                  Pmt Type
                                </th>
                                <th
                                  style={{
                                    padding: "8px",
                                    textAlign: "left",
                                    borderBottom: "2px solid #ddd",
                                    fontSize: "12px",
                                    textAlign: "center",
                                  }}
                                >
                                  Paid on
                                </th>

                                <th
                                  style={{
                                    padding: "8px",
                                    textAlign: "left",
                                    borderBottom: "2px solid #ddd",
                                    fontSize: "12px",
                                    textAlign: "center",
                                  }}
                                >
                                  Mode
                                </th>
                                <th
                                  style={{
                                    padding: "8px",
                                    textAlign: "left",
                                    borderBottom: "2px solid #ddd",
                                    fontSize: "12px",
                                    textAlign: "center",
                                  }}
                                >
                                  USD
                                </th>
                                <th
                                  style={{
                                    padding: "8px",
                                    textAlign: "left",
                                    borderBottom: "2px solid #ddd",
                                    whiteSpace: "pre-line",
                                    fontSize: "12px",
                                    textAlign: "center",
                                  }}
                                >
                                  AED
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {invoiceDetail?.booking?.paymentDetails &&
                                Array.isArray(
                                  invoiceDetail.booking.paymentDetails
                                ) &&
                                invoiceDetail.booking.paymentDetails.length >
                                  0 && (
                                  <tr>
                                    <td
                                      style={{
                                        padding: "8px",
                                        borderBottom: "1px solid #ddd",
                                        borderRight: "1px solid #ddd",
                                        borderLeft: "1px solid #ddd",
                                        fontSize: "12px",
                                        textAlign: "center",
                                      }}
                                    >
                                      Vehicle
                                    </td>
                                    <td
                                      style={{
                                        padding: "8px",
                                        borderBottom: "1px solid #ddd",
                                        borderRight: "1px solid #ddd",
                                        fontSize: "12px",
                                        textAlign: "center",
                                      }}
                                    >
                                      {moment(
                                        invoiceDetail?.booking
                                          ?.paymentDetails?.[0]?.payment
                                          ?.payment_date
                                      ).format("D-M-YY")}
                                    </td>
                                    <td
                                      style={{
                                        padding: "8px",
                                        borderBottom: "1px solid #ddd",
                                        borderRight: "1px solid #ddd",
                                        fontSize: "12px",
                                        textAlign: "center",
                                      }}
                                    >
                                      {invoiceDetail?.booking
                                        ?.paymentDetails?.[0]?.payment
                                        ?.payment_medium === "vault"
                                        ? "Wallet"
                                        : invoiceDetail?.booking
                                            ?.paymentDetails?.[0]?.payment
                                            ?.payment_medium}
                                    </td>
                                    <td
                                      style={{
                                        padding: "8px",
                                        textAlign: "center",
                                        borderBottom: "1px solid #ddd",
                                        borderRight: "1px solid #ddd",
                                        fontSize: "12px",
                                      }}
                                    >
                                      {CommaSeparator(
                                        (
                                          invoiceDetail?.booking?.paymentDetails?.reduce(
                                            (acc, curr) =>
                                              acc +
                                              parseFloat(curr?.amount || 0),
                                            0
                                          ) || 0
                                        ).toFixed(2)
                                      )}
                                    </td>
                                    <td
                                      style={{
                                        padding: "8px",
                                        textAlign: "center",
                                        borderBottom: "1px solid #ddd",
                                        borderRight: "1px solid #ddd",
                                        fontSize: "12px",
                                      }}
                                    >
                                      {CommaSeparator(
                                        (
                                          invoiceDetail?.booking?.paymentDetails?.reduce(
                                            (acc, curr) =>
                                              acc +
                                              parseFloat(curr?.amount_aed || 0),
                                            0
                                          ) || 0
                                        ).toFixed(2)
                                      )}
                                    </td>
                                  </tr>
                                )}

                              {payment.map((detail, index) => (
                                <tr key={index}>
                                  <td
                                    style={{
                                      padding: "8px",
                                      borderBottom: "1px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      borderLeft: "1px solid #ddd",
                                      fontSize: "12px",
                                      textAlign: "center",
                                    }}
                                  >
                                    {`${detail?.shipping ? "Shipping" : ""}${
                                      detail?.shipping &&
                                      (detail?.custom || detail?.vat)
                                        ? "+"
                                        : ""
                                    }${detail?.custom ? "C" : ""}${
                                      detail?.custom && detail?.vat ? "+" : ""
                                    }${detail?.vat ? "V" : ""}` ||
                                      "Reverse" ||
                                      "-"}
                                  </td>
                                  <td
                                    style={{
                                      padding: "8px",
                                      borderBottom: "1px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      fontSize: "12px",
                                      textAlign: "center",
                                    }}
                                  >
                                    {moment(detail?.payment_date).format(
                                      "D-M-YY"
                                    )}
                                  </td>
                                  <td
                                    style={{
                                      padding: "8px",
                                      borderBottom: "1px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      fontSize: "12px",
                                      textAlign: "center",
                                    }}
                                  >
                                    {detail?.payment_medium === "vault"
                                      ? "Wallet"
                                      : detail?.payment_medium}
                                  </td>
                                  <td
                                    style={{
                                      padding: "8px",
                                      borderBottom: "1px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      fontSize: "12px",
                                      textAlign: "center",
                                    }}
                                  >
                                    {CommaSeparator(
                                      parseFloat(detail?.amount)?.toFixed(2)
                                    ) || "00"}
                                  </td>
                                  <td
                                    style={{
                                      padding: "8px",
                                      borderBottom: "1px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      fontSize: "12px",
                                      textAlign: "center",
                                    }}
                                  >
                                    {CommaSeparator(
                                      parseFloat(
                                        detail?.amount * detail?.rate
                                      )?.toFixed(2)
                                    ) || "00"}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                  <Grid
                    container
                    sx={{
                      border: "solid",
                      borderWidth: 0.5,
                      borderColor: "lightgray",
                      marginTop: "10px",
                    }}
                  >
                    <Grid item xs={12} sx={{ padding: "5px" }} variant="body1">
                      <Typography
                        sx={{
                          color: "black",
                          fontWeight: "bold",
                        }}
                      >
                        Notes
                      </Typography>
                      {invoiceDetail?.payment?.map((pay, index) => (
                        <Typography
                          key={index}
                          sx={{ color: Colors.smokeyGrey, fontSize: "14px" }}
                        >
                          {pay.comments}
                        </Typography>
                      ))}
                    </Grid>
                  </Grid>

                  <Grid item xs={12} sx={{ marginTop: "10px" }}>
                    <Typography
                      variant="body1"
                      sx={{
                        color: "black",
                        fontFamily: FontFamily.NunitoRegular,
                        fontWeight: "bold",
                      }}
                    >
                      PLEASE READ CAREFULLY THE TERMS & CONDITIONS:
                    </Typography>
                    <Box
                      sx={{
                        fontWeight: 500,
                        fontSize: "12px",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ mb: 1, fontFamily: FontFamily.NunitoRegular }}
                      >
                        1- All vehicle details, charges, and rates have been
                        clearly explained and acknowledged at the time of
                        purchase.
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ mb: 1, fontFamily: FontFamily.NunitoRegular }}
                      >
                        2- Please ensure full payment is made within 3 business
                        days from the date of purchase to avoid late payment
                        charges and storage fees, which may apply upon vehicle
                        arrival at the final destination. (Note: If there are
                        special announcements or memos, those will take
                        precedence.)
                      </Typography>{" "}
                      <Typography
                        variant="body2"
                        sx={{ mb: 1, fontFamily: FontFamily.NunitoRegular }}
                      >
                        3- In the event of a relisting, relisting charges must
                        be settled within 3 days. A 15% penalty will be imposed
                        if payment is delayed beyond this period, in accordance
                        with the memo dated 9 June 2022.
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ mb: 1, fontFamily: FontFamily.NunitoRegular }}
                      >
                        4- Galaxy Customer Care will keep you updated on current
                        rates, charges, and relevant changes through WhatsApp
                        and email.
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ mb: 1, fontFamily: FontFamily.NunitoRegular }}
                      >
                        5- Kindly verify your invoice and cash before leaving
                        the counter. Discrepancies reported later may not be
                        entertained.
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ mb: 1, fontFamily: FontFamily.NunitoRegular }}
                      >
                        6- While we strive for accuracy, our establishment is
                        not liable for delays, mistakes, or omissions in
                        transmission. Our responsibility is limited to making
                        necessary corrections as promptly as possible.
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                      }}
                    >
                      <QRCodeCanvas
                        value={
                          window.location.origin +
                          `/client-invoice-v2-preview/${btoa(id)}`
                        }
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            </Box>

            <Box sx={{ pl: 4, pr: 3, py: 1, bgcolor: Colors.primary, mt: 4 }}>
              <Typography
                variant="caption"
                sx={{ color: Colors.white, fontFamily: FontFamily.openSans }}
              >
                Customer care Contact: Mohammed husni - +971523195682 (Arabic &
                English ) Ardamehr Shoev - +971545836028 (English ,Arabic, Tajik
                & Farsi)
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: Colors.white, fontFamily: FontFamily.openSans }}
              >
                Ravin abdul kareem - +971528293801 (Kurdish , Arabic & English)
                Magsat Gylyjov - +97158666403 (Turken , Russian & English)
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: Colors.white, fontFamily: FontFamily.openSans }}
              >
                Ind Area#4 P.O Box 83126, Sharjah, UAE.
              </Typography>
            </Box>
          </Box>
        </Box>
      </div>
    </Container>
  );
}

export default ExportShippingInvoiceV2;
